import { FileText, Loader2, Maximize2, X } from "lucide-react";
import * as React from "react";
import { documentPreviewUrl } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

export function PdfPreview({
  documentId,
  fileName,
  className,
  display = "inline",
  autoOpen = false,
  onClose,
}: {
  documentId: string;
  fileName: string;
  className?: string;
  display?: "inline" | "compact";
  autoOpen?: boolean;
  onClose?: () => void;
}) {
  const previewUrl = documentPreviewUrl(documentId);
  const isLocalHost = typeof window !== "undefined" && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  const [preview, setPreview] = React.useState<{ url: string; contentType: string; blob: Blob } | null>(null);
  const [error, setError] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [isMobilePreview, setIsMobilePreview] = React.useState(false);
  const hasAutoOpened = React.useRef(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobilePreview(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    hasAutoOpened.current = false;
    setPreview(null);
    setError("");
    setOpen(false);

    fetch(previewUrl, { credentials: "include" })
      .then(async (response) => {
        const contentType = response.headers.get("Content-Type") || "";
        if (!response.ok || !isPreviewableSource(contentType)) {
          let message = missingSourceMessage(isLocalHost);
          try {
            const payload = (await response.json()) as { error?: string };
            if (payload.error) {
              message = payload.error === "PDF file is unavailable." || payload.error === "Source file is unavailable."
                ? missingSourceMessage(isLocalHost)
                : payload.error;
            }
          } catch {
            // Non-JSON errors fall back to the friendly message above.
          }
          throw new Error(message);
        }
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreview({ url: objectUrl, contentType: blob.type, blob });
      })
      .catch((fetchError) => {
        if (cancelled) return;
        setError(fetchError instanceof Error ? fetchError.message : "Source preview failed.");
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isLocalHost, previewUrl]);

  React.useEffect(() => {
    if (!autoOpen || hasAutoOpened.current) return;
    if (!preview) return;
    hasAutoOpened.current = true;
    setOpen(true);
  }, [autoOpen, isMobilePreview, preview, previewUrl]);

  const isPdf = preview?.contentType.includes("application/pdf");
  const modalPreviewUrl = preview ? (isPdf ? `${preview.url}#view=FitH&zoom=page-width` : preview.url) : "";

  function closePreview() {
    setOpen(false);
    onClose?.();
  }

  const controls = (
    <div className="flex flex-col gap-2 sm:flex-row">
      {preview ? (
        <Button type="button" variant="secondary" size="sm" className="min-h-11" onClick={() => setOpen(true)}>
          <Maximize2 className="size-4" aria-hidden="true" />
          Preview source
        </Button>
      ) : (
        <Button type="button" variant="secondary" size="sm" className="min-h-11" disabled>
          <Maximize2 className="size-4" aria-hidden="true" />
          Preview source
        </Button>
      )}
    </div>
  );

  return (
    <div className={cn("rounded-lg border border-border bg-white p-3", className)}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FileText className="size-4 shrink-0 text-primary" aria-hidden="true" />
            <p className="font-semibold text-ink">Source file</p>
          </div>
          <p className="mt-1 truncate text-sm text-muted">{fileName}</p>
        </div>
        {controls}
      </div>
      {error ? (
        <div className="mt-3 rounded-lg border border-danger/20 bg-danger-bg p-4 text-sm leading-6 text-danger">
          {error}
        </div>
      ) : isMobilePreview ? (
        <div className="mt-3 rounded-lg border border-border bg-surface p-3 text-sm leading-6 text-muted">
          Use Preview source to review all pages without leaving the app.
        </div>
      ) : display === "inline" && preview ? (
        <div className="mt-3 rounded-lg border border-border bg-surface p-3 text-sm leading-6 text-muted">
          Use Preview source to review this file in an in-app window.
        </div>
      ) : display === "inline" ? (
        <div className="mt-3 rounded-lg border border-border bg-surface p-3 text-sm text-muted">
          Loading source preview...
        </div>
      ) : !preview ? (
        <p className="mt-3 text-sm text-muted">Loading source file...</p>
      ) : null}

      {open && preview ? (
        <div className="fixed inset-0 z-[80] bg-ink/45 p-0 sm:p-6" role="dialog" aria-modal="true" aria-label={`Source preview for ${fileName}`}>
          <div className="mx-auto flex h-dvh w-full max-w-none flex-col overflow-hidden rounded-none bg-white shadow-popover sm:h-full sm:max-w-5xl sm:rounded-xl">
            <div className="flex min-h-14 items-center justify-between gap-3 border-b border-border px-3 pt-[env(safe-area-inset-top)] sm:px-4 sm:pt-0">
              <div className="min-w-0">
                <p className="font-semibold text-ink">Review source file</p>
                <p className="truncate text-xs text-muted">{fileName}</p>
              </div>
              <button
                type="button"
                className="flex size-11 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-surface hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                aria-label="Close source preview"
                onClick={closePreview}
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
            {isPdf && isMobilePreview ? (
              <MobilePdfPages blob={preview.blob} fileName={fileName} />
            ) : isPdf ? (
              <iframe title={`Source preview for ${fileName}`} src={modalPreviewUrl} className="min-h-0 w-full flex-1 bg-surface" />
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-surface p-4">
                <img src={modalPreviewUrl} alt={`Source preview for ${fileName}`} className="max-h-full max-w-full object-contain" />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MobilePdfPages({ blob, fileName }: { blob: Blob; fileName: string }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    let renderToken = 0;
    const currentContainer = containerRef.current;
    if (!currentContainer) return undefined;
    const containerEl: HTMLDivElement = currentContainer;

    async function renderPdf() {
      const currentToken = ++renderToken;
      setLoading(true);
      setError("");
      containerEl.replaceChildren();

      try {
        const pdfjs = await import("pdfjs-dist");
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/build/pdf.worker.mjs",
          import.meta.url,
        ).toString();
        const data = await blob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data }).promise;
        const width = Math.max(280, Math.min(containerEl.clientWidth || window.innerWidth, 900));

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (cancelled || currentToken !== renderToken) return;
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1 });
          const scale = width / viewport.width;
          const scaledViewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (!context) continue;

          canvas.width = Math.floor(scaledViewport.width);
          canvas.height = Math.floor(scaledViewport.height);
          canvas.className = "mx-auto mb-3 block max-w-full rounded-md bg-white shadow-sm";
          canvas.setAttribute("aria-label", `${fileName} page ${pageNumber}`);
          containerEl.appendChild(canvas);
          await page.render({ canvasContext: context, viewport: scaledViewport }).promise;
        }

        if (!cancelled) setLoading(false);
      } catch (renderError) {
        if (cancelled) return;
        setError(renderError instanceof Error ? renderError.message : "Could not render PDF preview.");
        setLoading(false);
      }
    }

    void renderPdf();
    let resizeTimer: number | undefined;
    const onResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        void renderPdf();
      }, 150);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      if (resizeTimer) window.clearTimeout(resizeTimer);
      containerEl.replaceChildren();
    };
  }, [blob, fileName]);

  return (
    <div className="min-h-0 flex-1 overflow-auto bg-surface p-3">
      {loading ? (
        <div className="flex min-h-40 items-center justify-center gap-2 rounded-lg border border-border bg-white text-sm text-muted">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading all pages...
        </div>
      ) : null}
      {error ? (
        <div className="rounded-lg border border-danger/20 bg-danger-bg p-4 text-sm leading-6 text-danger">
          {error}
        </div>
      ) : null}
      <div ref={containerRef} className="mx-auto w-full max-w-3xl" />
    </div>
  );
}

function isPreviewableSource(contentType: string) {
  return contentType.includes("application/pdf") || contentType.startsWith("image/");
}

function missingSourceMessage(isLocalHost: boolean) {
  return isLocalHost
    ? "Source file is not available in local storage. If localhost is connected to the live database, files uploaded on the server must be uploaded again locally to preview."
    : "Source file is not available on the server. Upload it again to preview.";
}
